import { Injectable, OnDestroy, inject } from '@angular/core';
import { isEmpty } from 'lodash';
import { UserV2Service } from '@schenck/rotec-components';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { ReportTemplateApi } from 'src/api/report-template.api';
import { PaginationResult } from 'src/dto/pagination-result';
import {
  PdfExportPreview,
  ReportTemplate,
  ReportTemplateFieldDefinition,
  ReportTemplatePreview,
} from 'src/dto/report-template';
import { ReportTemplateAssignment } from 'src/dto/report-template/report-template-assignment';
import { TemplateToolboxSettings } from 'src/utils/interfaces/export-settings.interface';

@Injectable()
export class ReportTemplateService implements OnDestroy {
  private readonly reportTemplateApi = inject(ReportTemplateApi);
  private readonly userService = inject(UserV2Service);

  private numberOfTemplatesChanged$: BehaviorSubject<number>;

  constructor() {
    this.numberOfTemplatesChanged$ = new BehaviorSubject<number>(0);
    this.userService.currentUser$
      .pipe(
        take(1),
        filter(({ reportcenter }) => reportcenter.templates)
      )
      .subscribe(() => this.triggerNumberOfTemplatesChanged());
  }

  ngOnDestroy(): void {
    this.numberOfTemplatesChanged$.unsubscribe();
  }

  /**
   * Creates or updates a report template in the backend.
   *
   * @param reportTemplate the report template to create or update
   * @returns the created or updated template
   */
  createOrUpdate(reportTemplate: ReportTemplate): Observable<ReportTemplate> {
    return this.reportTemplateApi
      .createOrUpdate(reportTemplate)
      .pipe(tap(() => this.triggerNumberOfTemplatesChanged()));
  }

  /**
   * Clones a report template in the backend.
   *
   * @param id the the identifier of the template
   * @returns the cloned template
   */
  clone(id: string): Observable<ReportTemplate> {
    return this.reportTemplateApi.clone(id).pipe(tap(() => this.triggerNumberOfTemplatesChanged()));
  }

  /**
   * Deletes a report template in the backend.
   *
   * @param id the identifier of the template
   * @returns <b>true</b>, if the delete operation is successful, <b>false</b> otherwise
   */
  delete(id: string): Observable<boolean> {
    return this.reportTemplateApi
      .delete(id)
      .pipe(tap(() => this.triggerNumberOfTemplatesChanged()));
  }

  /**
   * Gets a report template from the backend.
   *
   * @param id the identifier of the template
   * @returns the template
   */
  get(id: string): Observable<ReportTemplate> {
    return this.reportTemplateApi.get(id);
  }

  /**
   * Gets the number of templates from the backend.
   *
   * @returns the number of templates
   */
  getNumberOfTemplates(): Observable<number> {
    return this.reportTemplateApi.getNumberOfTemplates();
  }

  /**
   * Gets template previews from the database. They contain only a subset of the template data, just enough to display them in a preview.
   *
   * @param pageOffset the page offset
   * @param pageSize the page size (if not specified, the default value is 10)
   * @param sortField the field which is supposed to sort the results (if not specified, the default value is 'id')
   * @param isDescendingSortOrder indicates whether the template previews shall be sorted in ascending order (false, default) or descending order (true).
   *
   * @returns the template previews that match the supplied pagination parameters, wrapped in a pagination container object
   */
  getPreviews(
    pageOffset: number = 0,
    pageSize: number = 10,
    name?: string,
    sortField: string = 'lastModifiedAt',
    isDescendingSortOrder: boolean = true
  ): Observable<PaginationResult<ReportTemplatePreview>> {
    const filter = {
      pageOffset: pageOffset,
      pageSize: pageSize,
      sortField: sortField,
      isDescendingSortOrder: isDescendingSortOrder,
      name,
    };

    return this.reportTemplateApi.getPreviews(filter);
  }

  /**
   * Gets field definitions from the database. They represent the actual content can be added to a report template.
   *
   * @returns the field definitions
   */
  getFieldDefinitions(): Observable<ReportTemplateFieldDefinition[]> {
    return this.reportTemplateApi.getFieldDefinitions();
  }

  /**
   * Sets report template assignments in the backend. Only records marked as 'dirty' are transferred.
   *
   * @param reportTemplateAssignments the report template assignments to store
   *
   * @returns the set report template assignments.
   */
  setTemplateAssignments(
    reportTemplateAssignments: ReportTemplateAssignment[]
  ): Observable<ReportTemplateAssignment[]> {
    reportTemplateAssignments = isEmpty(reportTemplateAssignments)
      ? reportTemplateAssignments
      : reportTemplateAssignments.filter(
          (reportTemplateAssigment) => reportTemplateAssigment.isDirty
        );

    return isEmpty(reportTemplateAssignments)
      ? of([])
      : this.reportTemplateApi.setTemplateAssignments(reportTemplateAssignments);
  }

  /**
   * Gets report template assignments from the backend. For every existing report, a record is returned,
   * even it has not been assigned to any machine or rotor type yet.
   *
   * @returns the report template assignments stored in the backend.
   */
  getTemplateAssignments(): Observable<ReportTemplateAssignment[]> {
    return this.reportTemplateApi.getTemplateAssignments();
  }

  /**
   * Exports a preview PDF based on the supplied template.
   *
   * @param reportTemplate the report template to use when generating a PDF preview.
   * @returns the exported PDF as Blob.
   */
  exportPreviewPdf(
    reportTemplate: ReportTemplate,
    settings: TemplateToolboxSettings
  ): Observable<Blob> {
    if (!reportTemplate) {
      return of();
    }

    return this.reportTemplateApi.exportPreviewPdf({
      ids: [],
      language: settings.language,
      decimals: settings.decimals,
      lenghtUnit: settings.lengthUnit,
      unbalUnit: settings.unbalUnit,
      weightUnit: settings.weightUnit,
      measuringRuns: settings.measuringRuns,
      template: reportTemplate,
      isUnbalanceAsMass: settings.isUnbalanceAsMass,
    } as PdfExportPreview);
  }

  /**
   * Checks if a report template with the supplied identifier exists.
   *
   * @param id the identifier of the report template
   * @returns <b>true</b>, if the delete operation is successful, <b>false</b> otherwise
   */
  exists(id: string): Observable<boolean> {
    return this.reportTemplateApi.getTemplateAssignments().pipe(
      map(
        (reportTemplateAssigments) =>
          !isEmpty(
            reportTemplateAssigments.filter(
              (reportTemplateAssigment) => reportTemplateAssigment.templateId === id
            )
          )
      ),
      catchError(() => of(false))
    );
  }

  /**
   * Observable interested parties can subscribe to, to get notified whenever the number of report templates changed.
   *
   * @returns the observable with the number of report templates,
   */
  onNumberOfTemplatesChanged(): Observable<number> {
    return this.numberOfTemplatesChanged$;
  }

  private triggerNumberOfTemplatesChanged(): void {
    this.getNumberOfTemplates().subscribe((numberOfTemplates) =>
      this.numberOfTemplatesChanged$.next(numberOfTemplates)
    );
  }
}
